import { z } from 'zod';

import { type Masked, unmask } from '~/contentful/graphql';
import type {
  Filter_EntryFragment,
  Filter_ExplicitValueFilterFragment,
  Filter_RangeValueFilterFragment,
} from '~/ui/systems/filter/query';
import { type Route } from '~/ui/systems/internalLink/route';

const searchParametersSchema = z.object({
  query: z.string().optional(),
});
const filtersSchema = z.record(
  z.string(),
  z.preprocess(arg => (Array.isArray(arg) ? arg : [arg]), z.string().array()),
);

export type SearchParameters = z.infer<typeof searchParametersSchema> & {
  filters: z.infer<typeof filtersSchema>;
};

/**
 * Parses the given search parameters into a {@link SearchParameters} object.
 *
 * @param searchParameters The search parameters that will be parsed into a
 *   {@link SearchParameters} object.
 */
export async function parseSearchParameters(
  searchParameters: PageProps<never>['searchParams'],
): Promise<SearchParameters> {
  const { query, ...rest } = searchParametersSchema.loose().parse(await searchParameters);
  return {
    query,
    filters: filtersSchema.parse(rest),
  };
}

/**
 * Returns the encoded search query applied to the given endpoint.
 *
 * @param endpoint The endpoint the apply the query to.
 * @param query The query to encode and apply to the endpoint.
 */
export function encodeSearchQuery(
  endpoint: Route.SEARCH | Route.GRANT_SEARCH,
  query: string,
): string {
  return `${endpoint}?query=${encodeURIComponent(query)}`;
}

/**
 * Extracts the filter values from the given {@link Filter_EntryFragment}.
 * @param filters
 */
export function extractGrantSearchFilterValues(
  filters: Masked<typeof Filter_EntryFragment>[],
): { value?: string; gte?: number; lte?: number }[] {
  const values: { value?: string; gte?: number; lte?: number }[] = [];
  filters.forEach(filter => {
    const data = unmask<typeof Filter_EntryFragment>(filter);
    switch (data.__typename) {
      case 'ExplicitValueFilter':
        const explicitValueFilter = unmask<typeof Filter_ExplicitValueFilterFragment>(data);
        values.push({
          value: explicitValueFilter.value ?? undefined,
        });
        break;

      case 'RangeValueFilter':
        const rangeValueFilter = unmask<typeof Filter_RangeValueFilterFragment>(data);
        values.push({
          gte: rangeValueFilter.gte ?? undefined,
          lte: rangeValueFilter.lte ?? undefined,
        });
        break;
    }
  });

  return values;
}

/**
 * Derives the inclusive range of timestamps in milliseconds from the start year through the end
 * year. For example, `deriveTimestampsFromYearRange(2021, 2023)` returns the first millisecond of
 * 2021 to the last millisecond of 2023.
 *
 * @param startYear The start year of the range.
 * @param endYear The end year of the range.
 * @returns The start and end timestamps, or {@link NaN} for undefined values.
 */
export function deriveTimestampsFromYearRange(
  startYear?: number,
  endYear?: number,
): { start: number; end: number } {
  return {
    start: Date.UTC(startYear || endYear || NaN),
    // Plus 1 to get the timestamp of the following year
    // Minus 1 to exclude the start of the following year
    end: Date.UTC((endYear || startYear || NaN) + 1) - 1,
  };
}
